// Document parsing service using Upstage API
export interface DocumentParseResponse {
    api: string;
    content: {
      html: string;
      markdown: string;
      text: string;
    };
    elements: DocumentElement[];
    model: string;
    usage: {
      pages: number;
    };
  }
  
  export interface DocumentElement {
    category: string;
    content: {
      html: string;
      markdown: string;
      text: string;
    };
    coordinates: Array<{
      x: number;
      y: number;
    }>;
    id: number;
    page: number;
  }
  
  const API_KEY = "up_kHTfu4tPlIfXirQ0OaesvYyJw6SWC";
  const API_URL = "https://api.upstage.ai/v1/document-digitization";
  
  export const parseDocument = async (file: File): Promise<DocumentParseResponse> => {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("model", "document-parse");
    formData.append("ocr", "force");
  
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${API_KEY}`,
        "Accept-Charset": "utf-8", // ensure UTF-8 response
      },
      body: formData,
    });
  
    if (!response.ok) {
      throw new Error(`Document parsing failed: ${response.statusText}`);
    }
  
    return response.json();
  };
  
  export const isDocumentFile = (file: File): boolean => {
    if (!file || !file.type) return false;
  
    const supportedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
      "image/bmp",
      "image/tiff",
      "image/heic",
    ];
  
    return supportedTypes.includes(file.type);
  };
  
  export const getDocumentIcon = (file: File): string => {
    if (!file || !file.type) return "📄";
  
    if (file.type === "application/pdf") return "📄";
    if (file.type.includes("word")) return "📝";
    if (file.type.includes("presentation")) return "📊";
    if (file.type.includes("spreadsheet")) return "📈";
    if (file.type.startsWith("image/")) return "🖼️";
    return "📄";
  };
  
  // Function to convert HTML to clean text with proper UTF-8 decoding
  export const htmlToText = (html: string): string => {
    if (!html) return "";
  
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
  
      let text = doc.body?.textContent || doc.body?.innerText || "";
  
      // Decode HTML entities (ensures α, β, γ, Ω appear correctly)
      const textarea = document.createElement("textarea");
      textarea.innerHTML = text;
      text = textarea.value;
  
      // Clean whitespace
      text = text.replace(/\s+/g, " ").trim();
  
      // Add line breaks for readability
      text = text.replace(/\. /g, ".\n\n");
      text = text.replace(/\n\s*\n\s*\n/g, "\n\n");
  
      return text;
    } catch (error) {
      console.error("Error converting HTML to text:", error);
      // Fallback: strip tags manually
      return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    }
  };
  